import React, { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../app/hooks';
import { useAskAica } from '../../../../../../../store/dashboard/askAica';
import { selectViewOnly } from '../../../../../../../store/dashboard/askAica/selectors';
import { setAskAicaSelectedSources, resetChatView, setSelectedConversation } from '../../../../../../../store/dashboard/askAica/actions';
import { useAppDispatch } from '../../../../../../../app/hooks';

import { AskAicaSurfaceProvider, AskAicaSurfaceContextValue, ASK_AICA_SURFACE } from './AskAicaSurfaceContext';

/**
 * Thin passthrough of useAskAica() + the leads-only slice reads (isClearingChat,
 * selectViewOnly, activeOrgId) onto the shared surface context. This is the SAME
 * data path AskAicaV2/LeftPanel/MessagesContainer/ReasoningPanel/AskAicaInputBox
 * read today — only the delivery mechanism changes (useAppSelector → useContext).
 * MUST NOT change any leads behaviour (RECUR-27369 §2/§4).
 */
export const LeadsAskAicaSurfaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const {
        messages,
        reasoning,
        isLoading,
        error,
        sendMessage,
        clearChatMemory,
        newChat,
        deleteConversation,
        pendingNewConversation,
        registerStreamStartedCallback,
        isLoadingChatHistory,
        messagesEndRef,
        fetchChatHistory,
        fetchLibraryFiles,
        fetchOrgConversations,
        hasMoreData,
    } = useAskAica();

    const isClearingChat = useAppSelector((state) => state.dashboard.askAicaReducer.isClearingChat);
    const isViewOnly = useAppSelector(selectViewOnly);
    const selectedSources = useAppSelector((state) => state.dashboard.askAicaReducer.uiState.selectedSources);
    const conversations = useAppSelector((state) => state.dashboard.askAicaReducer.conversations);
    const isLoadingConversations = useAppSelector((state) => state.dashboard.askAicaReducer.isLoadingConversations);
    const selectedConversationId = useAppSelector((state) => state.dashboard.askAicaReducer.selectedConversationId);
    const selectedConversationIsDeleted = useAppSelector((state) => state.dashboard.askAicaReducer.selectedConversationIsDeleted);
    const activeOrgId = useAppSelector((state) => state.dashboard.askAicaReducer.activeOrgId);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    // A cross-chat citation's navigation target (RECUR-27466 follow-up) — set by
    // openConversation's second arg, read + cleared by MessagesContainer once the
    // switched-to conversation's history has finished loading. Local component
    // state (not Redux): purely transient one-shot UI intent, scoped to this
    // panel instance.
    const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
    const clearFocusMessageId = useCallback(() => setFocusMessageId(null), []);
    // Mirrors useAskAica.ts's own org_id = params.id ?? investorOrgId resolution
    // (useAskAica doesn't expose that value itself).
    const fileContextOrgId = params.id ? params.id : investorOrgId;

    const value: AskAicaSurfaceContextValue = useMemo(
        () => ({
            surface: ASK_AICA_SURFACE.LEADS,
            messages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            hasMoreData,
            selectedSources,
            setSelectedSources: (sources: string[]) => dispatch(setAskAicaSelectedSources(sources)),
            sendMessage: (content: string, flowKey?: string, agentInput?: any) => sendMessage(content, flowKey, agentInput),
            clearChat: clearChatMemory,
            newChat,
            deleteConversation,
            pendingNewConversation,
            fetchChatHistory,
            fetchOrgConversations,
            fetchLibraryFiles,
            registerStreamStartedCallback,
            messagesEndRef,
            fileContextOrgId,
            conversations,
            isLoadingConversations,
            selectedConversationId,
            selectedConversationIsDeleted,
            isClearingChat,
            isViewOnly,
            activeOrgId,
            enableFeedbackMention: true,
            // Same sequence ConversationSidebar's handleRowClick uses (reset BEFORE
            // selecting, so fetchChatHistory's page-1 reset lands on a clean slate) —
            // duplicated here rather than shared because ConversationSidebar owns its
            // own dispatch/fetchChatHistory closures and isn't itself reusable as a fn.
            openConversation: (conversationId: string, targetMessageId?: string | null) => {
                dispatch(resetChatView());
                dispatch(setSelectedConversation(conversationId));
                fetchChatHistory(false, conversationId, true);
                setFocusMessageId(targetMessageId ?? null);
            },
            focusMessageId,
            clearFocusMessageId,
        }),
        [
            messages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            hasMoreData,
            selectedSources,
            dispatch,
            sendMessage,
            clearChatMemory,
            newChat,
            deleteConversation,
            pendingNewConversation,
            fetchChatHistory,
            fetchOrgConversations,
            fetchLibraryFiles,
            registerStreamStartedCallback,
            messagesEndRef,
            fileContextOrgId,
            conversations,
            isLoadingConversations,
            selectedConversationId,
            selectedConversationIsDeleted,
            isClearingChat,
            isViewOnly,
            activeOrgId,
            focusMessageId,
            clearFocusMessageId,
        ],
    );

    return <AskAicaSurfaceProvider value={value}>{children}</AskAicaSurfaceProvider>;
};

export default LeadsAskAicaSurfaceProvider;
